import { useRef, useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { API } from "@/App";
import {
  Plus, Trash2, GripVertical, Loader2, ImageUp, Camera, X, BadgeCheck, Contact,
  ArrowUp, ArrowDown, Play, Youtube,
} from "lucide-react";
import { toast } from "sonner";
import {
  MICROSITE_THEMES as themes,
  MICROSITE_TEMPLATES as templates,
  MICROSITE_BUTTON_STYLES,
  MICROSITE_BG_MODES,
  MICROSITE_FONTS,
  SOCIAL_PLATFORMS,
} from "@/components/MicrositeRenderer";
import { parseYouTubeId, getYouTubeThumbnail } from "@/lib/youtube";
import { BLOCK_TYPES, createBlock, isBlockComplete, getBlockType } from "@/lib/blocks";

const MAX_IMAGE_UPLOAD_SIZE = 5 * 1024 * 1024; // 5MB, sinkron dengan backend

/** Id pendek nan unik untuk satu blok — dipakai backend mencocokkan
 * statistik klik, supaya tetap akurat walau urutan blok diubah. */
const genLinkId = () => `blk_${Math.random().toString(36).slice(2, 10)}`;

// ============ IMAGE UPLOAD ============
export const ImageUpload = ({ label, value, onChange, endpoint, aspectRatio = "square", hint }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [preview, setPreview] = useState(value || "");
  const inputRef = useRef();

  useEffect(() => { setPreview(value || ""); }, [value]);

  const uploadFile = async (file) => {
    if (!file.type.startsWith("image/")) {
      toast.error("File harus berupa gambar");
      return;
    }
    if (file.size > MAX_IMAGE_UPLOAD_SIZE) {
      toast.error("Ukuran file maksimal 5MB");
      return;
    }

    // Pratinjau lokal langsung, kecuali HEIC — browser desktop umumnya tidak
    // bisa merender HEIC, jadi biarkan placeholder sampai hasil WebP kembali.
    const canPreviewLocally = !/heic|heif/i.test(file.type) && !/\.(heic|heif)$/i.test(file.name);
    const localUrl = canPreviewLocally ? URL.createObjectURL(file) : "";
    if (localUrl) setPreview(localUrl);

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}${endpoint}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" }
      });
      onChange(res.data.url);
      setPreview(res.data.url);
      toast.success("Gambar berhasil diupload!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Gagal upload gambar");
      setPreview(value || "");
    } finally {
      setIsUploading(false);
      if (localUrl) URL.revokeObjectURL(localUrl);
    }
  };

  const handleFile = (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (file) uploadFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
  };

  const isSquare = aspectRatio === "square";

  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div
        className={`group relative overflow-hidden cursor-pointer transition-all
          ${isSquare ? "w-28 h-28 rounded-full" : "w-full h-32 rounded-xl"}
          ${preview
            ? "ring-1 ring-border"
            : `border-2 border-dashed bg-muted/30 hover:bg-muted/50 ${
                isDragOver ? "border-primary bg-primary/5 scale-[1.02]" : "border-border hover:border-primary/60"
              }`
          }`}
        onClick={() => !isUploading && inputRef.current?.click()}
        onDrop={handleDrop}
        onDragOver={(e) => { e.preventDefault(); if (!isUploading) setIsDragOver(true); }}
        onDragLeave={() => setIsDragOver(false)}
      >
        {preview ? (
          <>
            <img src={preview} alt={label} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/50 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
              <Camera className="w-5 h-5 text-white drop-shadow" />
            </div>
            <button
              type="button"
              className="absolute top-1.5 right-1.5 bg-background/90 backdrop-blur border border-border rounded-full p-1 shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-destructive hover:text-destructive-foreground hover:border-destructive z-10"
              onClick={(e) => { e.stopPropagation(); handleRemove(); }}
              title="Hapus gambar"
            >
              <X className="w-3 h-3" />
            </button>
          </>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-muted-foreground px-2 text-center">
            {isUploading ? (
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            ) : (
              <>
                <div className="p-2 rounded-full bg-primary/10 text-primary">
                  <ImageUp className="w-4 h-4" />
                </div>
                <span className="text-[11px] leading-tight">
                  {isSquare ? "Upload" : "Seret gambar atau klik"}
                </span>
              </>
            )}
          </div>
        )}
        {isUploading && preview && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <Loader2 className="w-5 h-5 text-white animate-spin" />
          </div>
        )}
      </div>
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
      <input
        ref={inputRef}
        type="file"
        accept="image/*,.heic,.heif"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
};

// ============ TAB: PROFIL ============
export const ProfilTab = ({ form, patch, slugStatus, showSlug = true }) => {
  const addSocial = () => patch({ social_links: [...form.social_links, { platform: "instagram", value: "" }] });
  const updateSocial = (i, field, val) => {
    const n = [...form.social_links]; n[i] = { ...n[i], [field]: val }; patch({ social_links: n });
  };
  const removeSocial = (i) => patch({ social_links: form.social_links.filter((_, idx) => idx !== i) });

  return (
    <>
      <div className="space-y-2">
        <Label>Title *</Label>
        <Input placeholder="My Bio Links" value={form.title}
          onChange={e => patch({ title: e.target.value })} data-testid="microsite-title-input" />
      </div>

      <div className="space-y-2">
        <Label>Bio</Label>
        <Textarea placeholder="Deskripsi singkat tentang Anda..." value={form.bio}
          onChange={e => patch({ bio: e.target.value })} rows={3} data-testid="microsite-bio-input" />
      </div>

      {showSlug && (
        <div className="space-y-2">
          <Label>
            Custom URL
            <span className="text-xs text-muted-foreground ml-1">(opsional)</span>
          </Label>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {window.location.origin}/m/
            </span>
            <div className="relative flex-1">
              <Input
                placeholder="nama-kamu"
                value={form.custom_slug}
                onChange={(e) => patch({ custom_slug: e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, "") })}
              />
              {slugStatus === "checking" && (
                <span className="absolute right-2 top-2 text-xs text-muted-foreground">memeriksa...</span>
              )}
              {(slugStatus === "taken" || slugStatus === "invalid" || slugStatus === "too_short") && (
                <span className="absolute right-2 top-2 text-red-500">✕</span>
              )}
            </div>
          </div>
          {slugStatus === "available" && <p className="text-xs text-green-500">✓ URL tersedia</p>}
          {slugStatus === "taken" && <p className="text-xs text-red-500">URL sudah digunakan</p>}
          {slugStatus === "invalid" && <p className="text-xs text-red-500">Hanya huruf kecil, angka, dan tanda (-)</p>}
          {slugStatus === "too_short" && <p className="text-xs text-red-500">Minimal 3 karakter</p>}
          {!form.custom_slug && <p className="text-xs text-muted-foreground">Kosongkan untuk generate otomatis</p>}
        </div>
      )}

      <ImageUpload
        label="Foto Profil / Avatar"
        value={form.avatar_url}
        onChange={(url) => patch({ avatar_url: url })}
        endpoint="/upload/microsite-avatar"
        aspectRatio="square"
        hint="JPG/PNG/WebP, maks 5MB. Akan dioptimasi ke 400×400 WebP"
      />

      <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3.5 mt-2">
        <div className="flex items-center gap-2.5 min-w-0">
          <BadgeCheck className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Lencana terverifikasi</p>
            <p className="text-xs text-muted-foreground">Centang biru di sebelah nama</p>
          </div>
        </div>
        <Switch checked={form.verified} onCheckedChange={(v) => patch({ verified: v })} />
      </div>

      <div className="flex items-center justify-between gap-3 rounded-xl border border-border p-3.5">
        <div className="flex items-center gap-2.5 min-w-0">
          <Contact className="w-4 h-4 text-primary shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-medium">Tombol simpan kontak</p>
            <p className="text-xs text-muted-foreground">Pengunjung bisa unduh vCard sekali klik</p>
          </div>
        </div>
        <Switch checked={form.contact_enabled} onCheckedChange={(v) => patch({ contact_enabled: v })} />
      </div>

      <div className="space-y-2 pt-2">
        <div className="flex items-center justify-between">
          <Label>Media Sosial</Label>
          <Button type="button" variant="ghost" size="sm" onClick={addSocial}>
            <Plus className="w-4 h-4 mr-1" /> Tambah
          </Button>
        </div>
        {form.social_links.length === 0 && (
          <p className="text-xs text-muted-foreground">
            Tampil sebagai baris ikon di bawah bio. Belum ada yang ditambahkan.
          </p>
        )}
        {form.social_links.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            <Select value={s.platform} onValueChange={(v) => updateSocial(i, "platform", v)}>
              <SelectTrigger className="w-[130px] shrink-0">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SOCIAL_PLATFORMS.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder={
                s.platform === "email" ? "nama@email.com"
                : s.platform === "whatsapp" ? "62812xxxxxxx"
                : "@username"
              }
              value={s.value}
              onChange={e => updateSocial(i, "value", e.target.value)}
              className="flex-1"
            />
            <Button type="button" variant="ghost" size="icon"
              className="h-9 w-9 text-destructive flex-shrink-0" onClick={() => removeSocial(i)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        ))}
      </div>
    </>
  );
};

// ============ TAB: BLOK ============
const blockMeta = (type) => getBlockType(type);

// Editor field sesuai jenis blok. `update(fields)` menggabungkan field ke blok.
const BlockFields = ({ block, update }) => {
  switch (block.type) {
    case "link":
      return (
        <div className="flex gap-2">
          <Input placeholder="Title" value={block.title} onChange={(e) => update({ title: e.target.value })} className="flex-1" />
          <Input placeholder="URL" value={block.url} onChange={(e) => update({ url: e.target.value })} className="flex-1" />
        </div>
      );
    case "video":
      return <VideoFields block={block} update={update} />;
    case "audio":
      return (
        <div className="flex gap-2">
          <Input placeholder="Judul lagu / artis" value={block.title} onChange={(e) => update({ title: e.target.value })} className="flex-1" />
          <Input placeholder="URL Spotify / SoundCloud" value={block.url} onChange={(e) => update({ url: e.target.value })} className="flex-1" />
        </div>
      );
    case "image":
      return (
        <div className="space-y-2">
          <ImageUpload
            label="Gambar"
            value={block.url}
            onChange={(url) => update({ url })}
            endpoint="/upload/block-image"
            aspectRatio="wide"
            hint="Upload, atau tempel URL gambar langsung di bawah"
          />
          <Input placeholder="URL gambar (opsional jika tidak upload)" value={block.url} onChange={(e) => update({ url: e.target.value })} />
          <Input placeholder="Caption (opsional)" value={block.caption || ""} onChange={(e) => update({ caption: e.target.value })} />
          <Input placeholder="Tautan saat diklik (opsional)" value={block.link_url || ""} onChange={(e) => update({ link_url: e.target.value })} />
        </div>
      );
    case "gallery":
      return <GalleryFields block={block} update={update} />;
    case "text":
      return (
        <div className="space-y-2">
          <Textarea placeholder="Tulis teks..." value={block.content || ""} onChange={(e) => update({ content: e.target.value })} rows={3} />
          <AlignToggle value={block.style} onChange={(style) => update({ style })} />
        </div>
      );
    case "heading":
      return (
        <div className="space-y-2">
          <Input placeholder="Judul / heading" value={block.title} onChange={(e) => update({ title: e.target.value })} />
          <AlignToggle value={block.style} onChange={(style) => update({ style })} />
        </div>
      );
    case "divider":
      return <Input placeholder="Label opsional di tengah garis" value={block.label || ""} onChange={(e) => update({ label: e.target.value })} />;
    case "social":
      return <SocialItemsEditor block={block} update={update} />;
    case "map":
      return (
        <div className="space-y-2">
          <Input placeholder="Judul lokasi (opsional)" value={block.title} onChange={(e) => update({ title: e.target.value })} />
          <Input placeholder="Alamat, nama tempat, atau URL embed Google Maps" value={block.url} onChange={(e) => update({ url: e.target.value })} />
        </div>
      );
    case "faq":
      return <FaqEditor block={block} update={update} />;
    case "newsletter":
      return (
        <div className="space-y-2">
          <Input placeholder="Judul (mis. Berlangganan)" value={block.title} onChange={(e) => update({ title: e.target.value })} />
          <Input placeholder="Deskripsi singkat" value={block.description || ""} onChange={(e) => update({ description: e.target.value })} />
        </div>
      );
    case "countdown":
      return (
        <div className="space-y-2">
          <Input placeholder="Judul acara (opsional)" value={block.title} onChange={(e) => update({ title: e.target.value })} />
          <Input type="datetime-local" value={block.deadline || ""} onChange={(e) => update({ deadline: e.target.value })} />
        </div>
      );
    case "quote":
      return (
        <div className="space-y-2">
          <Textarea placeholder="Isi kutipan..." value={block.content || ""} onChange={(e) => update({ content: e.target.value })} rows={2} />
          <Input placeholder="Nama / sumber (opsional)" value={block.author || ""} onChange={(e) => update({ author: e.target.value })} />
        </div>
      );
    default:
      return null;
  }
};

const AlignToggle = ({ value, onChange }) => (
  <div className="flex items-center gap-1">
    <span className="text-xs text-muted-foreground mr-1">Perataan:</span>
    {[{ v: "left", l: "Kiri" }, { v: "center", l: "Tengah" }].map((o) => (
      <button key={o.v} type="button" onClick={() => onChange(o.v)}
        className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${value === o.v ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
        {o.l}
      </button>
    ))}
  </div>
);

const VideoFields = ({ block, update }) => {
  const videoId = parseYouTubeId(block.url);
  const thumb = videoId ? getYouTubeThumbnail(videoId) : null;
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input placeholder="Judul video" value={block.title} onChange={(e) => update({ title: e.target.value })} className="flex-1" />
        <Input placeholder="URL YouTube / TikTok" value={block.url} onChange={(e) => update({ url: e.target.value })} className="flex-1" />
      </div>
      <div className="flex items-center gap-2">
        {thumb ? (
          <div className="relative w-20 aspect-video rounded-md overflow-hidden border border-border shrink-0">
            <img src={thumb} alt="Pratinjau video" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            <span className="absolute inset-0 grid place-items-center bg-black/30">
              <Play className="w-4 h-4 text-white" fill="currentColor" strokeWidth={0} />
            </span>
          </div>
        ) : (
          <span className="w-20 aspect-video rounded-md border border-dashed border-border grid place-items-center text-muted-foreground shrink-0">
            <Youtube className="w-4 h-4" />
          </span>
        )}
        <p className="text-[11px] text-muted-foreground leading-snug">
          {thumb ? "Kartu thumbnail akan tampil di halaman." : "Tempel URL YouTube/TikTok untuk memunculkan thumbnail."}
        </p>
      </div>
    </div>
  );
};

const GalleryFields = ({ block, update }) => {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const items = block.items || [];

  const upload = async (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;
    setUploading(true);
    const urls = [];
    for (const file of files) {
      if (file.size > MAX_IMAGE_UPLOAD_SIZE) { toast.error("Ukuran file maksimal 5MB"); continue; }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const res = await axios.post(`${API}/upload/block-image`, fd, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
        urls.push(res.data.url);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Gagal upload gambar");
      }
    }
    if (urls.length) update({ items: [...items, ...urls] });
    setUploading(false);
  };

  return (
    <div className="space-y-2">
      {items.length > 0 && (
        <div className="grid grid-cols-4 gap-2">
          {items.map((src, idx) => (
            <div key={idx} className="relative group aspect-square rounded-lg overflow-hidden border border-border">
              <img src={src} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
              <button type="button"
                className="absolute top-1 right-1 bg-background/90 border border-border rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity hover:text-destructive"
                onClick={() => update({ items: items.filter((_, j) => j !== idx) })}>
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
      <Button type="button" variant="outline" size="sm" className="w-full" disabled={uploading}
        onClick={() => inputRef.current?.click()}>
        {uploading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <ImageUp className="w-4 h-4 mr-1" />}
        Tambah Foto
      </Button>
      <input ref={inputRef} type="file" accept="image/*,.heic,.heif" multiple className="hidden"
        onChange={(e) => { upload(e.target.files); e.target.value = ""; }} />
    </div>
  );
};

const SocialItemsEditor = ({ block, update }) => {
  const items = block.items || [];
  const add = () => update({ items: [...items, { platform: "instagram", value: "" }] });
  const set = (i, field, val) => {
    const n = [...items]; n[i] = { ...n[i], [field]: val }; update({ items: n });
  };
  const remove = (i) => update({ items: items.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-2">
      {items.map((s, i) => (
        <div key={i} className="flex items-center gap-2">
          <Select value={s.platform} onValueChange={(v) => set(i, "platform", v)}>
            <SelectTrigger className="w-[130px] shrink-0"><SelectValue /></SelectTrigger>
            <SelectContent>
              {SOCIAL_PLATFORMS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Input placeholder="@username" value={s.value} onChange={(e) => set(i, "value", e.target.value)} className="flex-1" />
          <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => remove(i)}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="w-4 h-4 mr-1" /> Tambah sosial
      </Button>
    </div>
  );
};

const FaqEditor = ({ block, update }) => {
  const items = block.items || [];
  const add = () => update({ items: [...items, { question: "", answer: "" }] });
  const set = (i, field, val) => {
    const n = [...items]; n[i] = { ...n[i], [field]: val }; update({ items: n });
  };
  const remove = (i) => update({ items: items.filter((_, idx) => idx !== i) });
  return (
    <div className="space-y-2">
      {items.map((f, i) => (
        <div key={i} className="space-y-1.5 rounded-lg border border-border p-2">
          <div className="flex items-center gap-2">
            <Input placeholder="Pertanyaan" value={f.question} onChange={(e) => set(i, "question", e.target.value)} className="flex-1" />
            <Button type="button" variant="ghost" size="icon" className="h-9 w-9 text-destructive shrink-0" onClick={() => remove(i)}>
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
          <Textarea placeholder="Jawaban" value={f.answer} onChange={(e) => set(i, "answer", e.target.value)} rows={2} />
        </div>
      ))}
      <Button type="button" variant="outline" size="sm" onClick={add}>
        <Plus className="w-4 h-4 mr-1" /> Tambah tanya-jawab
      </Button>
    </div>
  );
};

export const BlokTab = ({ form, patch }) => {
  const addBlock = (type) => patch({ links: [...form.links, createBlock(type)] });
  const updateBlock = (i, fields) => {
    const n = [...form.links]; n[i] = { ...n[i], ...fields }; patch({ links: n });
  };
  const removeBlock = (i) => patch({ links: form.links.filter((_, idx) => idx !== i) });
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= form.links.length) return;
    const n = [...form.links];
    [n[i], n[j]] = [n[j], n[i]];
    patch({ links: n });
  };

  const activeCount = form.links.filter((l) => l.active !== false && isBlockComplete(l)).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Daftar Blok</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            {activeCount} blok aktif · pilih jenis konten yang ingin ditambahkan
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" size="sm" data-testid="add-block-btn">
              <Plus className="w-4 h-4 mr-1" /> Add Block
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60 max-h-[70vh] overflow-y-auto">
            <DropdownMenuLabel className="text-xs text-muted-foreground">Pilih jenis blok</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {BLOCK_TYPES.map((bt) => {
              const Icon = bt.Icon;
              return (
                <DropdownMenuItem key={bt.value} onClick={() => addBlock(bt.value)} className="cursor-pointer">
                  <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-muted text-foreground shrink-0">
                    <Icon className="w-4 h-4" />
                  </span>
                  <span className="flex-1 min-w-0">
                    <span className="flex items-center gap-1.5">
                      <span className="font-medium text-sm">{bt.label}</span>
                      {bt.isPro && (
                        <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-primary/15 text-primary">PRO</span>
                      )}
                    </span>
                    <span className="block text-xs text-muted-foreground truncate">{bt.desc}</span>
                  </span>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {form.links.map((block, i) => {
        const isInactive = block.active === false;
        const meta = blockMeta(block.type);
        const Icon = meta?.Icon;
        return (
          <div key={block.id || i} className={`rounded-lg border border-border p-2.5 transition-opacity ${isInactive ? "opacity-50" : ""}`}>
            <div className="flex items-center gap-2">
              <GripVertical className="w-4 h-4 text-muted-foreground flex-shrink-0 cursor-grab" />
              <div className="flex flex-col gap-1 shrink-0">
                <button type="button" disabled={i === 0}
                  className="h-4 w-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
                  onClick={() => moveBlock(i, -1)} title="Naikkan urutan">
                  <ArrowUp className="w-3.5 h-3.5" />
                </button>
                <button type="button" disabled={i === form.links.length - 1}
                  className="h-4 w-6 flex items-center justify-center text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:hover:text-muted-foreground"
                  onClick={() => moveBlock(i, 1)} title="Turunkan urutan">
                  <ArrowDown className="w-3.5 h-3.5" />
                </button>
              </div>

              <span className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-muted text-xs font-medium text-muted-foreground shrink-0">
                {Icon && <Icon className="w-3.5 h-3.5" />} {meta?.label}
              </span>

              <div className="flex-1" />

              <Switch
                checked={!isInactive}
                onCheckedChange={(v) => updateBlock(i, { active: v })}
                title={isInactive ? "Nonaktif — sembunyikan dari halaman publik" : "Aktif"}
              />
              <Button type="button" variant="ghost" size="icon"
                className="h-9 w-9 text-destructive flex-shrink-0" onClick={() => removeBlock(i)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>

            <div className="mt-2.5 pl-5">
              <BlockFields block={block} update={(fields) => updateBlock(i, fields)} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Alias lama — beberapa tempat masih mengimpor nama TautanTab.
export const TautanTab = BlokTab;

// ============ TAB: TAMPILAN ============
export const TampilanTab = ({ form, patch }) => (
  <div className="space-y-5">
    <div className="space-y-2">
      <Label>Template</Label>
      <div className="grid grid-cols-2 gap-2">
        {templates.map(t => (
          <button key={t.value} type="button"
            className={`p-3 rounded-xl border text-left transition-all ${form.template === t.value ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"}`}
            onClick={() => patch({ template: t.value })}>
            <p className="text-sm font-medium">{t.label}</p>
            <p className="text-xs text-muted-foreground">{t.desc}</p>
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>Tema Warna</Label>
      <div className="flex flex-wrap gap-2">
        {themes.map(t => (
          <button key={t.value} type="button"
            className={`w-8 h-8 rounded-full border-2 transition-all ${form.theme === t.value ? "border-foreground scale-110" : "border-transparent"}`}
            style={{ backgroundColor: t.color }}
            title={t.label}
            onClick={() => patch({ theme: t.value })} />
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>Gaya Tombol</Label>
      <div className="grid grid-cols-4 gap-2">
        {MICROSITE_BUTTON_STYLES.map(s => (
          <button key={s.value} type="button"
            className={`py-2.5 rounded-lg border text-xs font-medium transition-all ${form.button_style === s.value ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"}`}
            onClick={() => patch({ button_style: s.value })}>
            {s.label}
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>
        Latar Halaman
        {form.background_url && <span className="text-xs text-muted-foreground ml-1.5">(nonaktif — background custom aktif)</span>}
      </Label>
      <div className="flex flex-wrap gap-2">
        {MICROSITE_BG_MODES.map(m => (
          <button key={m.value} type="button" disabled={!!form.background_url}
            className={`px-3.5 py-2 rounded-lg border text-xs font-medium transition-all disabled:opacity-40 ${form.bg_mode === m.value ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"}`}
            onClick={() => patch({ bg_mode: m.value })}>
            {m.label}
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <Label>Tipografi</Label>
      <div className="flex flex-wrap gap-2">
        {MICROSITE_FONTS.map(f => (
          <button key={f.value} type="button"
            className={`px-3.5 py-2 rounded-lg border text-xs font-medium transition-all ${form.font === f.value ? "border-primary bg-primary/10" : "border-border hover:border-muted-foreground"}`}
            style={{ fontFamily: f.value === "grotesk" ? "'Space Grotesk', sans-serif" : f.value === "serif" ? "Georgia, serif" : "'Plus Jakarta Sans', sans-serif" }}
            onClick={() => patch({ font: f.value })}>
            {f.label}
          </button>
        ))}
      </div>
    </div>

    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label>Sudut Tombol</Label>
        <span className="text-xs font-mono text-muted-foreground">{form.radius}px</span>
      </div>
      <Slider value={[form.radius]} onValueChange={(v) => patch({ radius: v[0] })} min={0} max={28} step={1} />
    </div>

    <ImageUpload
      label="Cover / Banner"
      value={form.cover_url}
      onChange={(url) => patch({ cover_url: url })}
      endpoint="/upload/cover"
      aspectRatio="wide"
      hint="Dioptimasi ke 1200×400 WebP (template Card)"
    />

    <ImageUpload
      label="Background Custom"
      value={form.background_url}
      onChange={(url) => patch({ background_url: url })}
      endpoint="/upload/background"
      aspectRatio="wide"
      hint="Dioptimasi ke 1920×1080 WebP. Menimpa pilihan latar halaman di atas."
    />
  </div>
);

export const DEFAULT_MICROSITE_FORM = {
  title: "",
  bio: "",
  avatar_url: "",
  cover_url: "",
  background_url: "",
  theme: "purple",
  template: "classic",
  links: [{ id: genLinkId(), title: "", url: "", active: true, type: "link" }],
  custom_slug: "",
  verified: false,
  contact_enabled: false,
  social_links: [],
  button_style: "soft",
  bg_mode: "auto",
  font: "jakarta",
  radius: 14,
};

export const microsToForm = (ms) => ({
  title: ms.title || "",
  bio: ms.bio || "",
  avatar_url: ms.avatar_url || "",
  cover_url: ms.cover_url || "",
  background_url: ms.background_url || "",
  theme: ms.theme || "purple",
  template: ms.template || "classic",
  // Blok lama (dibuat sebelum fitur blok/reorder ada) mungkin belum punya id
  // atau type — beri sekarang di sisi klien supaya UI langsung berfungsi;
  // backend menyimpannya permanen begitu form ini disimpan. Field lain (items,
  // caption, dsb.) dipertahankan lewat spread.
  links: ms.links?.length > 0
    ? ms.links.map(l => ({ ...l, id: l.id || genLinkId(), type: l.type || "link", active: l.active !== false }))
    : [{ id: genLinkId(), title: "", url: "", active: true, type: "link" }],
  custom_slug: ms.slug || "",
  verified: !!ms.verified,
  contact_enabled: !!ms.contact_enabled,
  social_links: ms.social_links || [],
  button_style: ms.button_style || "soft",
  bg_mode: ms.bg_mode || "auto",
  font: ms.font || "jakarta",
  radius: ms.radius ?? 14,
});

export const formToPayload = (form) => ({
  title: form.title,
  bio: form.bio || null,
  avatar_url: form.avatar_url || null,
  cover_url: form.cover_url || null,
  background_url: form.background_url || null,
  theme: form.theme,
  template: form.template,
  links: form.links.filter(isBlockComplete),
  custom_slug: form.custom_slug || null,
  verified: form.verified,
  contact_enabled: form.contact_enabled,
  social_links: form.social_links.filter(s => s.value.trim()),
  button_style: form.button_style,
  bg_mode: form.bg_mode,
  font: form.font,
  radius: form.radius,
});
